/* -------------------------------------------------------------------------- */
/*                              Basic Constants                               */
/* -------------------------------------------------------------------------- */

export const BUSINESS_DAY_START_HOUR = 7;
export const BUSINESS_DAY_START_MINUTE = 0;

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const padNumber = (value) =>
  String(value).padStart(2, "0");

const isValidDate = (date) =>
  date instanceof Date &&
  !Number.isNaN(date.getTime());

const cloneDate = (date) =>
  new Date(date.getTime());

const parseDateValue = (
  dateValue = new Date()
) => {
  if (dateValue instanceof Date) {
    const clonedDate = cloneDate(dateValue);

    if (!isValidDate(clonedDate)) {
      throw new Error(
        "Invalid date supplied."
      );
    }

    return clonedDate;
  }

  if (
    typeof dateValue === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      dateValue.trim()
    )
  ) {
    const parsedDate = new Date(
      `${dateValue.trim()}T12:00:00+05:30`
    );

    if (!isValidDate(parsedDate)) {
      throw new Error(
        "Invalid business date supplied."
      );
    }

    return parsedDate;
  }

  const parsedDate = new Date(dateValue);

  if (!isValidDate(parsedDate)) {
    throw new Error(
      "Invalid date supplied."
    );
  }

  return parsedDate;
};

const buildDateKey = (date) => {
  if (!isValidDate(date)) {
    throw new Error(
      "Invalid date supplied to buildDateKey."
    );
  }

  return [
    date.getFullYear(),
    padNumber(date.getMonth() + 1),
    padNumber(date.getDate()),
  ].join("-");
};

/* -------------------------------------------------------------------------- */
/*                         Business Date Calculation                          */
/* -------------------------------------------------------------------------- */

/**
 * Hotel business day:
 *
 * 7:00 AM se next day 6:59:59 AM tak.
 *
 * Example:
 * 22 July 2026, 06:30 AM => Business Date 21 July 2026
 * 22 July 2026, 07:00 AM => Business Date 22 July 2026
 */
export const getBusinessDateKey = (
  dateValue = new Date()
) => {
  const date = parseDateValue(dateValue);
  // Shift to India wall time then subtract the 7 AM business-day boundary.
  const shifted = new Date(date.getTime() + (330 - BUSINESS_DAY_START_HOUR * 60 - BUSINESS_DAY_START_MINUTE) * 60000);
  return shifted.toISOString().slice(0,10);
};

/* -------------------------------------------------------------------------- */
/*                         Business Day Start Date                            */
/* -------------------------------------------------------------------------- */

export const getBusinessDayStart = (
  businessDateValue = new Date()
) => {
  let businessDateKey;

  if (
    typeof businessDateValue ===
      "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      businessDateValue.trim()
    )
  ) {
    businessDateKey =
      businessDateValue.trim();
  } else {
    businessDateKey =
      getBusinessDateKey(
        businessDateValue
      );
  }

  const startDate = new Date(`${businessDateKey}T${padNumber(BUSINESS_DAY_START_HOUR)}:${padNumber(BUSINESS_DAY_START_MINUTE)}:00+05:30`);
  if (!isValidDate(startDate)) throw new Error('Invalid business date.');

  return startDate;
};

/* -------------------------------------------------------------------------- */
/*                          Business Day End Date                             */
/* -------------------------------------------------------------------------- */

export const getBusinessDayEnd = (
  businessDateValue = new Date()
) => {
  const startDate =
    getBusinessDayStart(
      businessDateValue
    );

  const endDate = cloneDate(startDate);

  endDate.setTime(endDate.getTime() + 86400000);

  endDate.setMilliseconds(
    endDate.getMilliseconds() - 1
  );

  return endDate;
};

/* -------------------------------------------------------------------------- */
/*                         Complete Business Range                            */
/* -------------------------------------------------------------------------- */

export const getBusinessDayRange = (
  businessDateValue = new Date()
) => {
  const businessDate =
    typeof businessDateValue ===
      "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      businessDateValue.trim()
    )
      ? businessDateValue.trim()
      : getBusinessDateKey(
          businessDateValue
        );

  const start =
    getBusinessDayStart(
      businessDate
    );

  const end =
    getBusinessDayEnd(
      businessDate
    );

  return {
    businessDate,

    start,
    end,

    startIso: start.toISOString(),
    endIso: end.toISOString(),

    label:
      formatBusinessDateLabel(
        businessDate
      ),
  };
};

/* -------------------------------------------------------------------------- */
/*                       Previous Business Date Key                           */
/* -------------------------------------------------------------------------- */

export const getPreviousBusinessDateKey = (value = new Date()) => {
  const key = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : getBusinessDateKey(value);
  const date = new Date(`${key}T12:00:00Z`);
  if (!isValidDate(date) || date.toISOString().slice(0, 10) !== key) throw new Error('Invalid business date.');
  date.setUTCDate(date.getUTCDate() + (-1));
  return date.toISOString().slice(0, 10);
};

export const getNextBusinessDateKey = (value = new Date()) => {
  const key = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : getBusinessDateKey(value);
  const date = new Date(`${key}T12:00:00Z`);
  if (!isValidDate(date) || date.toISOString().slice(0, 10) !== key) throw new Error('Invalid business date.');
  date.setUTCDate(date.getUTCDate() + (1));
  return date.toISOString().slice(0, 10);
};

export const isDateInsideBusinessDay = (
  dateValue,
  businessDateValue
) => {
  const date = parseDateValue(dateValue);

  const { start, end } =
    getBusinessDayRange(
      businessDateValue
    );

  return date >= start && date <= end;
};

/* -------------------------------------------------------------------------- */
/*                      Business Date Comparison                              */
/* -------------------------------------------------------------------------- */

export const compareBusinessDateKeys = (
  firstBusinessDate,
  secondBusinessDate
) => {
  const firstDate = parseDateValue(
    firstBusinessDate
  );

  const secondDate = parseDateValue(
    secondBusinessDate
  );

  if (
    firstDate.getTime() ===
    secondDate.getTime()
  ) {
    return 0;
  }

  return firstDate < secondDate
    ? -1
    : 1;
};

/* -------------------------------------------------------------------------- */
/*                     Convert Date to Input Value                            */
/* -------------------------------------------------------------------------- */

export const toDateInputValue = (
  dateValue = new Date()
) => {
  const date = parseDateValue(dateValue);

  return buildDateKey(date);
};

/* -------------------------------------------------------------------------- */
/*                 Convert Date-Time to Local Input Value                     */
/* -------------------------------------------------------------------------- */

export const toDateTimeLocalValue = (
  dateValue = new Date()
) => {
  const date = parseDateValue(dateValue);

  return [
    buildDateKey(date),
    [
      padNumber(date.getHours()),
      padNumber(date.getMinutes()),
    ].join(":"),
  ].join("T");
};

/* -------------------------------------------------------------------------- */
/*                        Format Business Date                                */
/* -------------------------------------------------------------------------- */

export const formatBusinessDate = (
  businessDateValue,
  options = {}
) => {
  const {
    includeYear = true,
    weekday = false,
  } = options;

  const date = parseDateValue(
    businessDateValue
  );

  return date.toLocaleDateString(
    "en-IN",
    {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",

      year: includeYear
        ? "numeric"
        : undefined,

      weekday: weekday
        ? "short"
        : undefined,
    }
  );
};

/* -------------------------------------------------------------------------- */
/*                       Business Day Label                                   */
/* -------------------------------------------------------------------------- */

export const formatBusinessDateLabel = (
  businessDateValue
) => {
  const businessDate =
    typeof businessDateValue ===
      "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      businessDateValue.trim()
    )
      ? businessDateValue.trim()
      : getBusinessDateKey(
          businessDateValue
        );

  const start =
    getBusinessDayStart(
      businessDate
    );

  const end =
    getBusinessDayEnd(
      businessDate
    );

  const startText =
    start.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const endText =
    end.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return `${startText} – ${endText}`;
};

/* -------------------------------------------------------------------------- */
/*                         Current Business Day                               */
/* -------------------------------------------------------------------------- */

export const getCurrentBusinessDay = (
  now = new Date()
) => {
  const businessDate =
    getBusinessDateKey(now);

  return getBusinessDayRange(
    businessDate
  );
};

/* -------------------------------------------------------------------------- */
/*                   Firestore Query Range Helper                             */
/* -------------------------------------------------------------------------- */

export const getBusinessDayQueryRange = (
  businessDateValue
) => {
  const { start, end } =
    getBusinessDayRange(
      businessDateValue
    );

  return {
    startDate: start,
    endDate: end,

    startIso: start.toISOString(),
    endIso: end.toISOString(),

    startTimestamp:
      start.getTime(),

    endTimestamp:
      end.getTime(),
  };
};

/* -------------------------------------------------------------------------- */
/*                        Assign Business Date                                */
/* -------------------------------------------------------------------------- */

export const assignBusinessDate = (
  object = {},
  dateField = "eventAt"
) => {
  const eventDate =
    object?.[dateField] ||
    object?.createdAt ||
    new Date();

  return {
    ...object,

    businessDate:
      getBusinessDateKey(
        eventDate
      ),
  };
};

/* -------------------------------------------------------------------------- */
/*                              Default Export                                */
/* -------------------------------------------------------------------------- */

export const getBusinessTime = (businessDate, time) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(businessDate) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) throw new Error('Valid business date/time required hai.');
  const [hours, minutes] = time.split(':').map(Number);
  const start = getBusinessDayStart(businessDate);
  if (getBusinessDateKey(start) !== businessDate) throw new Error('Invalid business date.');
  const offset = (hours * 60 + minutes - BUSINESS_DAY_START_HOUR * 60 - BUSINESS_DAY_START_MINUTE + 1440) % 1440;
  return new Date(start.getTime() + offset * 60000);
};

const businessDateService = {
  BUSINESS_DAY_START_HOUR,
  BUSINESS_DAY_START_MINUTE,

  assignBusinessDate,
  compareBusinessDateKeys,
  formatBusinessDate,
  formatBusinessDateLabel,
  getBusinessDateKey,
  getBusinessDayEnd,
  getBusinessDayQueryRange,
  getBusinessDayRange,
  getBusinessDayStart,
  getCurrentBusinessDay,
  getNextBusinessDateKey,
  getPreviousBusinessDateKey,
  isDateInsideBusinessDay,
  toDateInputValue,
  toDateTimeLocalValue,
};

export default businessDateService;